import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getTasks, getSeguimientoStatus } from '../utils/api';

const MONTHS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
const CLOSED = ['Aprobada / Cerrada', 'No realizada'];

const pctColor = (p) => p >= 75 ? '#10B981' : p >= 40 ? '#F59E0B' : '#EF4444';
const pctBg   = (p) => p >= 75 ? 'rgba(16,185,129,0.1)' : p >= 40 ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)';

const formatWeekRange = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const fri = new Date(d); fri.setDate(d.getDate() + 6);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} — ${fri.getDate()} ${MONTHS[fri.getMonth()]}`;
};

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Buenos días';
  if (h < 18) return 'Buenas tardes';
  return 'Buenas noches';
};

// ─── Tarjeta de acceso rápido ─────────────────────────────────────────────────
const QuickCard = ({ icon, label, meta, accent, onClick, badge }) => (
  <motion.button whileTap={{ scale: 0.97 }} onClick={onClick}
    className="flex-1 min-w-[140px] rounded-2xl p-4 text-left relative overflow-hidden transition-shadow"
    style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
    {badge != null && badge > 0 && (
      <span className="absolute top-3 right-3 text-[10px] font-black px-1.5 py-0.5 rounded-full"
        style={{ background: accent + '20', color: accent }}>
        {badge}
      </span>
    )}
    <div className="text-2xl mb-2">{icon}</div>
    <p className="text-sm font-bold" style={{ color: '#0F172A' }}>{label}</p>
    <p className="text-xs mt-0.5 leading-snug" style={{ color: '#94A3B8' }}>{meta}</p>
    <div className="mt-3 flex items-center gap-1" style={{ color: accent }}>
      <span className="text-xs font-bold">Abrir</span>
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7"/>
      </svg>
    </div>
  </motion.button>
);

// ─── Barra de progreso con etiqueta ──────────────────────────────────────────
const ProgressBar = ({ label, value, total, pct, color }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <span className="text-xs font-bold" style={{ color: '#475569' }}>{label}</span>
      <span className="text-xs font-black" style={{ color }}>
        {total != null ? `${value}/${total}` : `${pct}%`}
      </span>
    </div>
    <div className="rounded-full overflow-hidden" style={{ height: 6, background: 'rgba(0,0,0,0.06)' }}>
      <motion.div initial={{ width: 0 }}
        animate={{ width: `${pct}%` }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        style={{ height: '100%', background: color, borderRadius: 9999 }}
      />
    </div>
  </div>
);

// ─── Fila de responsabilidad ──────────────────────────────────────────────────
const RespRow = ({ name, count, pct, onClick }) => {
  const color = pctColor(pct);
  return (
    <motion.div whileTap={{ scale: 0.99 }} onClick={onClick}
      className="flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-colors"
      style={{ background: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.05)' }}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold truncate" style={{ color: '#1E293B' }}>{name}</p>
        <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>
          {count} tarea{count !== 1 ? 's' : ''}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="w-20 rounded-full overflow-hidden" style={{ height: 4, background: 'rgba(0,0,0,0.06)' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 9999 }} />
        </div>
        <span className="text-sm font-black w-9 text-right" style={{ color }}>{pct}%</span>
      </div>
    </motion.div>
  );
};

// ─── Dashboard usuario ────────────────────────────────────────────────────────
const DashboardUsuario = ({ user }) => {
  const navigate  = useNavigate();
  const [tasks, setTasks]           = useState([]);
  const [segData, setSegData]       = useState(null);
  const [loading, setLoading]       = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [tasksRes, segRes] = await Promise.all([getTasks(), getSeguimientoStatus()]);
      setTasks(tasksRes.data || []);
      setSegData(segRes.data);
    } catch {}
    setLoading(false);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // ── Derivar métricas ──────────────────────────────────────────────────────
  const activeTasks   = tasks.filter(t => !CLOSED.includes(t.status));
  const blockedTasks  = activeTasks.filter(t => t.status === 'Bloqueada');
  const overdueTasks  = activeTasks.filter(t => t.due_date && new Date(t.due_date) < new Date(new Date().setHours(0,0,0,0)));
  const criticalTasks = activeTasks.filter(t => t.priority === 'Crítica');

  const segItems      = segData?.items || [];
  const totalSeg      = segItems.length;
  const updatedSeg    = segItems.filter(i => i.has_update).length;
  const pendingSeg    = totalSeg - updatedSeg;
  const segPct        = totalSeg > 0 ? Math.round((updatedSeg / totalSeg) * 100) : 100;
  const weekLabel     = formatWeekRange(segData?.week_start);

  // Cumplimiento promedio de tareas activas
  const tasksWithPct   = activeTasks.filter(t => t.cumplimiento_pct != null && t.cumplimiento_pct > 0);
  const avgCumplimiento = tasksWithPct.length > 0
    ? Math.round(tasksWithPct.reduce((s, t) => s + t.cumplimiento_pct, 0) / tasksWithPct.length)
    : 0;

  // Agrupar por responsabilidad
  const respMap = {};
  activeTasks.forEach(t => {
    const key = t.responsabilidad || 'Sin responsabilidad';
    if (!respMap[key]) respMap[key] = { count: 0, pcts: [] };
    respMap[key].count++;
    if (t.cumplimiento_pct != null) respMap[key].pcts.push(t.cumplimiento_pct);
  });
  const respList = Object.entries(respMap)
    .map(([name, { count, pcts }]) => ({
      name,
      count,
      pct: pcts.length > 0 ? Math.round(pcts.reduce((s, p) => s + p, 0) / pcts.length) : 0,
    }))
    .sort((a, b) => a.pct - b.pct); // menor cumplimiento primero — requieren más atención

  // Tareas prioritarias para mostrar en el banner (vencidas + críticas, sin duplicados)
  const prioritySet = new Set([...overdueTasks, ...criticalTasks].map(t => t.id));
  const priorityTasks = [...prioritySet].map(id => activeTasks.find(t => t.id === id)).filter(Boolean).slice(0, 3);

  return (
    <div className="min-h-screen" style={{ background: '#F1F5F9' }}>

      {/* Header */}
      <div className="sticky top-14 md:top-0 z-10 px-4 md:px-6 py-4"
        style={{ background: 'rgba(10,26,20,0.95)', backdropFilter: 'blur(20px)', borderBottom: '1.5px solid rgba(0,208,132,0.2)' }}>
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-4">
          <div>
            <h1 className="text-base font-black" style={{ color: '#F0FDF4' }}>
              {greeting()}, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-xs font-semibold mt-0.5" style={{ color: '#6EE7B7' }}>
              {weekLabel}
            </p>
          </div>
          <button onClick={() => navigate('/tasks/create')} className="btn-primary flex items-center gap-1.5 text-sm flex-shrink-0">
            <span className="text-base leading-none">+</span> Nueva Tarea
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5 space-y-4">

        {/* ── Banner de seguimiento ───────────────────────────────────────── */}
        {pendingSeg > 0 ? (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl px-5 py-4 flex items-start justify-between gap-4"
            style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.1), rgba(245,158,11,0.04))', border: '1.5px solid rgba(245,158,11,0.3)' }}>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#F59E0B' }} />
                <p className="text-sm font-black" style={{ color: '#D97706' }}>
                  {pendingSeg} tarea{pendingSeg !== 1 ? 's' : ''} sin seguimiento esta semana
                </p>
              </div>
              <p className="text-xs" style={{ color: '#B45309' }}>
                Actualiza tu avance en Mi Semana antes del viernes
              </p>
            </div>
            <button onClick={() => navigate('/mi-semana')}
              className="btn-primary flex-shrink-0 text-sm">
              Registrar →
            </button>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl px-5 py-4 flex items-center gap-3"
            style={{ background: 'rgba(16,185,129,0.07)', border: '1.5px solid rgba(16,185,129,0.2)' }}>
            <svg className="w-5 h-5 flex-shrink-0" style={{ color: '#10B981' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <p className="text-sm font-bold" style={{ color: '#059669' }}>Seguimiento completo esta semana ✓</p>
          </motion.div>
        )}

        {/* ── Accesos rápidos ─────────────────────────────────────────────── */}
        <section>
          <p className="text-[10px] font-black uppercase tracking-widest mb-2.5" style={{ color: '#94A3B8' }}>
            Accesos rápidos
          </p>
          <div className="flex gap-3 overflow-x-auto pb-1">
            <QuickCard
              icon="📅"
              label="Mi Semana"
              meta={pendingSeg > 0 ? `${pendingSeg} sin actualizar` : 'Todo al día'}
              accent={pendingSeg > 0 ? '#F59E0B' : '#10B981'}
              badge={pendingSeg || null}
              onClick={() => navigate('/mi-semana')}
            />
            <QuickCard
              icon="📋"
              label="Mis Tareas"
              meta={`${activeTasks.length} activas${overdueTasks.length > 0 ? ` · ${overdueTasks.length} vencidas` : ''}`}
              accent={overdueTasks.length > 0 ? '#EF4444' : '#6366F1'}
              badge={overdueTasks.length || null}
              onClick={() => navigate('/tasks')}
            />
            <QuickCard
              icon="⬛"
              label="Kanban"
              meta={blockedTasks.length > 0 ? `${blockedTasks.length} bloqueada${blockedTasks.length !== 1 ? 's' : ''}` : `${activeTasks.filter(t => t.status === 'En progreso').length} en curso`}
              accent={blockedTasks.length > 0 ? '#EF4444' : '#3B82F6'}
              badge={blockedTasks.length || null}
              onClick={() => navigate('/kanban')}
            />
          </div>
        </section>

        {/* ── Mi progreso esta semana ──────────────────────────────────────── */}
        <section className="rounded-2xl p-5" style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)' }}>
          <p className="text-[10px] font-black uppercase tracking-widest mb-4" style={{ color: '#94A3B8' }}>
            Mi progreso esta semana
          </p>
          <div className="space-y-4">
            <ProgressBar
              label="Seguimiento semanal"
              value={updatedSeg}
              total={totalSeg}
              pct={segPct}
              color={pctColor(segPct)}
            />
            <ProgressBar
              label="Cumplimiento promedio de tareas"
              pct={avgCumplimiento}
              color={pctColor(avgCumplimiento)}
            />
          </div>
          <div className="flex gap-3 mt-4">
            <div className="flex-1 rounded-xl p-3 text-center" style={{ background: pctBg(segPct) }}>
              <p className="text-xl font-black" style={{ color: pctColor(segPct) }}>{segPct}%</p>
              <p className="text-[10px] font-bold mt-0.5 uppercase tracking-wide" style={{ color: '#94A3B8' }}>Seguimiento</p>
            </div>
            <div className="flex-1 rounded-xl p-3 text-center" style={{ background: pctBg(avgCumplimiento) }}>
              <p className="text-xl font-black" style={{ color: pctColor(avgCumplimiento) }}>{avgCumplimiento}%</p>
              <p className="text-[10px] font-bold mt-0.5 uppercase tracking-wide" style={{ color: '#94A3B8' }}>Avance</p>
            </div>
            <div className="flex-1 rounded-xl p-3 text-center" style={{ background: overdueTasks.length > 0 ? 'rgba(239,68,68,0.08)' : 'rgba(16,185,129,0.08)' }}>
              <p className="text-xl font-black" style={{ color: overdueTasks.length > 0 ? '#EF4444' : '#10B981' }}>
                {overdueTasks.length}
              </p>
              <p className="text-[10px] font-bold mt-0.5 uppercase tracking-wide" style={{ color: '#94A3B8' }}>Vencidas</p>
            </div>
          </div>
        </section>

        {/* ── Mis responsabilidades ────────────────────────────────────────── */}
        {respList.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-2.5">
              <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: '#94A3B8' }}>
                Mis responsabilidades
              </p>
              <button onClick={() => navigate('/tasks')}
                className="text-xs font-bold" style={{ color: '#00C878' }}>
                Ver todas →
              </button>
            </div>
            <div className="rounded-2xl overflow-hidden" style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)' }}>
              <div className="p-3 space-y-2">
                {respList.map(r => (
                  <RespRow
                    key={r.name}
                    name={r.name}
                    count={r.count}
                    pct={r.pct}
                    onClick={() => navigate('/tasks')}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ── Atención urgente ─────────────────────────────────────────────── */}
        {priorityTasks.length > 0 && (
          <section>
            <p className="text-[10px] font-black uppercase tracking-widest mb-2.5" style={{ color: '#EF4444' }}>
              Requieren atención
            </p>
            <div className="rounded-2xl overflow-hidden" style={{ background: '#fff', border: '1.5px solid rgba(239,68,68,0.15)' }}>
              <div className="p-3 space-y-2">
                {priorityTasks.map(task => {
                  const isOverdueTask = task.due_date && new Date(task.due_date) < new Date(new Date().setHours(0,0,0,0));
                  return (
                    <motion.div key={task.id} whileTap={{ scale: 0.99 }}
                      onClick={() => navigate('/tasks')}
                      className="flex items-start gap-3 px-4 py-3 rounded-xl cursor-pointer"
                      style={{ background: 'rgba(239,68,68,0.04)', border: '1px solid rgba(239,68,68,0.1)' }}>
                      <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5"
                        style={{ background: task.priority === 'Crítica' ? '#EF4444' : '#F97316' }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate" style={{ color: '#1E293B' }}>{task.title}</p>
                        {task.responsabilidad && (
                          <p className="text-xs mt-0.5" style={{ color: '#94A3B8' }}>{task.responsabilidad}</p>
                        )}
                      </div>
                      <div className="flex-shrink-0 text-right">
                        {isOverdueTask && (
                          <span className="text-xs font-bold" style={{ color: '#EF4444' }}>Vencida</span>
                        )}
                        {task.priority === 'Crítica' && !isOverdueTask && (
                          <span className="text-xs font-bold" style={{ color: '#F97316' }}>Crítica</span>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {activeTasks.length === 0 && (
          <div className="text-center py-20 rounded-2xl" style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.07)' }}>
            <p className="text-4xl mb-3">🎉</p>
            <p className="font-bold" style={{ color: '#1E293B' }}>Sin tareas activas</p>
            <p className="text-sm mt-1 mb-5" style={{ color: '#94A3B8' }}>Cuando te asignen tareas aparecerán aquí</p>
            <button onClick={() => navigate('/tasks/create')} className="btn-primary text-sm">+ Crear tarea</button>
          </div>
        )}

        <div style={{ height: 40 }} />
      </div>
    </div>
  );
};

export default DashboardUsuario;
